import type { Collection, CollectionHealth, Company, DashboardData, Event, Exclusion, Job, JobFit, OpportunityProfile, ResearchCandidate, Taxonomy } from './types';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly detail?: unknown) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      accept: 'application/json',
      ...init?.headers,
    },
  });
  const payload = await response.json().catch(() => null) as { error?: string; detail?: unknown } | null;
  if (!response.ok) {
    throw new ApiError(payload?.error ?? `Request failed with status ${response.status}`, response.status, payload?.detail);
  }
  return payload as T;
}

// Declared as type aliases rather than interfaces on purpose: an interface has
// no implicit index signature, so it cannot be handed to a function that reads
// its entries generically, which is exactly what building a query string is.

/** Filters the registry listing accepts. An empty value means "do not narrow". */
/**
 * Filters the registry listing accepts.
 *
 * Each enum axis is a set, sent as a repeated parameter: `OR` within one, `AND`
 * across them. An empty array asks nothing — `query` tests arrays for length
 * rather than truthiness, so unticking the last box sends no parameter at all
 * rather than an empty one the API refuses as a taxonomy value.
 */
export type CompanyFilters = {
  q?: string;
  domain?: string[];
  stage?: string[];
  reviewStatus?: string[];
};

/**
 * Filters the observed-posting listing accepts.
 *
 * `function` and `seniority` select on what the title classifiers made of a
 * posting. Both were computed and stored from the first release with no way to
 * query them, so the families that say a company has left the laboratory —
 * manufacturing, field deployment — could not be asked for.
 */
export type JobFilters = {
  q?: string;
  status?: string;
  /**
   * Role families to include. A set: the API takes the parameter repeated, and
   * an empty one narrows nothing. Sorted before it is sent, so ticking the same
   * two boxes in either order is one request and one cache entry rather than
   * two of each for one result.
   */
  function?: string[];
  seniority?: string[];
  remotePolicy?: string[];
  /** A floor on the stated salary: postings *known* to pay at least this much. */
  paysAtLeast?: string;
};

/**
 * Builds a query string, dropping keys the caller left empty.
 *
 * Sending `?domain=` is not the same request as sending no `domain` at all, and
 * the difference is the kind that only shows up once a filter is cleared.
 */
const query = (
  params: Readonly<Record<string, string | string[] | undefined>>,
  extra: Record<string, string> = {},
) => {
  const search = new URLSearchParams(extra);
  for (const [key, value] of Object.entries(params)) {
    // An array is tested for length, never for truthiness: `[]` is truthy, so
    // the obvious `if (value)` sends `?function=` the moment a reader unticks
    // their last box — an empty string the API refuses as a taxonomy value,
    // turning "no filter" into a 422.
    if (Array.isArray(value)) {
      for (const member of [...value].sort()) search.append(key, member);
    } else if (value) {
      search.set(key, value);
    }
  }
  return search.toString();
};

// Every list is still capped at the API maximum. The cap is now a bound on one
// page of a *narrowed* result set rather than on the set the browser filters
// itself, so the total beside it describes the whole match.
const PAGE = { limit: '500' };

export const queries = {
  dashboard: () => api<DashboardData>('/api/dashboard'),
  // Whole rather than paged, and recomputed per request: freshness is relative
  // to the instant it is asked about, and a page of feeds would describe that
  // page's health while reading as the system's.
  collection: () => api<CollectionHealth>('/api/collection'),
  companies: (filters: CompanyFilters = {}) =>
    api<Collection<Company>>(`/api/companies?${query(filters, PAGE)}`),
  company: (slug: string) => api<Company>(`/api/companies/${encodeURIComponent(slug)}`),
  events: () => api<Collection<Event>>('/api/events?limit=500'),
  // Returned whole rather than paged: the grade is derived, so the queue cannot
  // be selected on in a query and a page of it would understate the work left.
  eventsNeedingCorroboration: () => api<Collection<Event>>('/api/events/needs-corroboration'),
  jobs: (filters: JobFilters = {}) => api<Collection<Job>>(`/api/jobs?${query(filters, PAGE)}`),
  profiles: () => api<Collection<OpportunityProfile>>('/api/opportunity-profiles'),
  // Ranked server side over the whole matching set, then windowed. Ranking in
  // the browser would rank one page, which is the substitution the listing
  // totals elsewhere exist to make visible.
  jobFit: (profile: string, filters: JobFilters = {}) =>
    api<Collection<JobFit>>(`/api/jobs/fit?${query({ ...filters, profile }, PAGE)}`),
  research: () => api<Collection<ResearchCandidate>>('/api/research-candidates'),
  exclusions: (reasonCode: string[] = []) =>
    api<Collection<Exclusion>>(`/api/exclusions?${query({ reasonCode }, PAGE)}`),
  taxonomy: () => api<Taxonomy>('/api/taxonomy'),
};
